import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const MyBikesSection = ({ bikes = [] }) => {
  const navigate = useNavigate();

  const handleViewRecommendations = (bikeId) => {
    navigate('/upgrade-recommendations', { state: { bikeId } });
  };

  const handleUploadNew = () => {
    navigate('/photo-upload');
  };

  const getBikeTypeIcon = (type) => {
    switch (type) {
      case 'road': return 'Zap';
      case 'mountain': return 'Mountain';
      case 'hybrid': return 'Bike';
      case 'gravel': return 'MapPin';
      default: return 'Bike';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'analyzed': return 'text-success';
      case 'pending': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'analyzed': return 'Analysis Complete';
      case 'pending': return 'Processing...';
      case 'error': return 'Analysis Failed';
      default: return 'Unknown Status';
    }
  };

  if (bikes?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">My Bikes</h2>
        </div>
        
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Bike" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No bikes uploaded yet</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Upload your first bike photo to get personalized upgrade recommendations and start optimizing your ride.
          </p>
          <Button
            variant="default"
            iconName="Upload"
            iconPosition="left"
            onClick={handleUploadNew}
          >
            Upload Your First Bike
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">My Bikes ({bikes?.length})</h2>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={handleUploadNew}
        >
          Add New Bike
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {bikes?.map((bike) => (
          <div key={bike?.id} className="bg-background border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
            <div className="relative h-48 overflow-hidden">
              <Image
                src={bike?.image}
                alt={bike?.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 right-3">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  bike?.status === 'analyzed' ? 'bg-success/10 text-success' :
                  bike?.status === 'pending'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                }`}>
                  {getStatusLabel(bike?.status)}
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name={getBikeTypeIcon(bike?.type)} size={16} color="var(--color-accent)" />
                <h3 className="font-semibold text-foreground">{bike?.name}</h3>
              </div>
              
              <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-4">
                <span className="capitalize">{bike?.type}</span>
                <span>•</span>
                <span>{bike?.year}</span>
                {bike?.recommendationCount > 0 && (
                  <>
                    <span>•</span>
                    <span className="text-accent font-medium">
                      {bike?.recommendationCount} recommendations
                    </span>
                  </>
                )}
              </div>

              <div className="flex space-x-2">
                {bike?.status === 'analyzed' && (
                  <Button
                    variant="default"
                    size="sm"
                    fullWidth
                    onClick={() => handleViewRecommendations(bike?.id)}
                  >
                    View Recommendations
                  </Button>
                )}
                {bike?.status === 'pending' && (
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    disabled
                  >
                    Processing...
                  </Button>
                )}
                {bike?.status === 'error' && (
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    onClick={() => navigate('/photo-upload', { state: { retryBike: bike } })}
                  >
                    Retry Analysis
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyBikesSection;